import fr from 'facet-rest';
import useConfirmationDialogState from 'Hooks/useConfirmationDialogState';
import {
  FDBodyText,
  FDConfirmationDialog, FDLabelText,
  FDSecondaryActionButton,
  FDStack,
} from '@facet-wealth/design';
import PropTypes from 'prop-types';
import React from 'react';
import { buildFacetDesignOverride, overrideReadOnly } from 'Packages/FacetRest/FacetDesignOverride';
import MetaControl from 'Components/UI/Meta/MetaControl';
import {
  PaymentDetailStatus,
} from 'Views/Page/Content/Templates/Profile/Templates/ProfileTemplate/Subpages/PaymentInfoProfileSubpage/PaymentDetailStatus';
import { buildUseStyles, useTheme } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import {
  PaymentMethodCard,
} from 'Views/Page/Content/Templates/Profile/Templates/ProfileTemplate/Subpages/PaymentInfoProfileSubpage/PaymentMethodCard';
import { TextErrorAlert } from 'Packages/FacetDesign/Components/Experimental/TextErrorAlert';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { PaymentDetailsAction } from './PaymentDetailsAction';

const metaOverrides = buildFacetDesignOverride()
  .nameOverride('status', {
    _component: () => PaymentDetailStatus,
  }, overrideReadOnly)
  .nameOverride('paymentMethodInformation', {
    _component: () => FDBodyText,
  }, overrideReadOnly)
  .nameOverride('paymentMethodExpiredOn', {
    _component: () => TextErrorAlert,
  }, overrideReadOnly)
  .nameOverride('paymentMethod', {
    _component: () => FDLabelText,
  }, overrideReadOnly);


const useStyles = buildUseStyles('PaymentDetailRow', (theme) => ({
  img: {
    maxHeight: theme.spacing(3),
    objectFit: 'contain',
  },
}));


export const PaymentDetailRow = ({
  resource: paymentDetail,
}) => {
  const { refreshPageContentCollection } = usePageContentContext();
  const theme = useTheme();
  const classes = useStyles(theme);
  const { displayErrorMessage, displaySuccess } = useSnackbarContext();

  const handleRemove = async () => {
    try {
      await paymentDetail.withRelationship(fr.relationships.removePaymentMethod.name,
        (rel) => rel.link('related').delete(),
        () => displayErrorMessage('Unable to remove payment method'));
      displaySuccess('Successfully removed payment method');
    } catch (err) {
      displayErrorMessage(err, err.errors);
    }
  };

  const {
    dialogOpen,
    loading,
    handleClose,
    handleSubmit,
    handleOpen,
  } = useConfirmationDialogState({
    onSubmit: handleRemove,
  });

  return (
    <PaymentMethodCard isCurrent={paymentDetail.attr('isCurrent') === 'true'}>
      <FDStack direction={'column'} spacing={2}>
        <FDStack spacing={1} alignItems={'center'}>
          <PaymentDetailStatus resource={paymentDetail} />
          <MetaControl resource={paymentDetail} attrName={'paymentMethod'} metaOverrideSet={metaOverrides} />
        </FDStack>
        <FDStack spacing={2} alignItems={'center'}>
          {paymentDetail.withRelationship(fr.relationships.logo.name,
            // We have a gap right now in FDImage where border radius of 16px is applied to all images and you cannot
            // opt out of it. These images are fairly small, so they look very silly with such a large border radius
            (rel) => (<img
              className={classes.img}
              src={rel.link('related').url()}
              alt={rel.meta('prompt') || ''}
            />),
            () => null)}
          <MetaControl resource={paymentDetail} attrName={'paymentMethodInformation'} metaOverrideSet={metaOverrides} flex={1} />
          <MetaControl resource={paymentDetail} attrName={'paymentMethodExpiredOn'} metaOverrideSet={metaOverrides} />
        </FDStack>
        <FDStack spacing={1}>
          <PaymentDetailsAction resource={paymentDetail} />
          {paymentDetail.withRelationship(fr.relationships.removePaymentMethod.name, (rel) => (
            <FDSecondaryActionButton
              label={rel.meta('prompt')}
              size={'sm'}
              onClick={handleOpen}
              icon={rel.meta('icon')}
              disabled={!(rel.meta('actions')?.includes('DELETE'))}
            />
          ), () => null)}
          <FDConfirmationDialog
            open={dialogOpen}
            title={'Remove payment method?'}
            message={`You will no longer be able to pay using ${paymentDetail.attr('paymentMethodInformation')}`}
            confirmLabel={'Remove'}
            loading={loading}
            onConfirm={async () => {
              await handleSubmit();
              await refreshPageContentCollection();
              handleClose();
            }}
            onClose={() => {
              handleClose();
            }}
          />
        </FDStack>
      </FDStack>
    </PaymentMethodCard>
  );
};

PaymentDetailRow.propTypes = {
  resource: PropTypes.instanceOf(fr.Resource).isRequired,
};
